import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { addDays, format, isBefore, startOfDay } from 'date-fns'
import jstz from 'jstz'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'

import DatePicker from '../../../common/components/DatePicker'
import { Combobox } from '../../../common/components/combobox'
import {
    Report,
    useReportGetReportsForAdminQuery,
    useLazyReportGetReportDataQuery,
    useReportGetReportsForPartnerQuery,
} from '../../../common/redux/api/web-api'
import Text from '../../../ui/Text'
import { WidgetLayout } from '../../../lib/widget-layout'
import { SLabelText } from '../../../common/styles/Form'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import { isAdminDomain, isPartnerDomain } from '../../../lib/currentDomain'
import {
    EntitlementsEnum,
    hasAccessToEntitlement,
    REPORT_HISTORY_RESTRICTION_DAYS,
} from '../../../lib/entitlementUtils'
import { RootState as AdminRootState } from '../../../admin/redux/store'
import { RootState as PartnerRootState } from '../../../partner/redux/store'
import { useEntitlements } from '../../hooks/useEntitlements'
import useMediaQueryHook from '../../../common/hooks/useMediaQueryHook'
import { ScreenDeviceType } from '../../../common/enums/screenDeviceTypeEnum'
import { InformationDialog } from '../../dialogs/information-dialog'
import useText from '../../hooks/useText'
import { SelectButton, SelectButtonOption } from '../../uikit/SelectButton'

import {
    generateReports,
    PeriodInterval,
    getDatesForInterval,
    getRestrictedReportHistoryCutoffDate,
    isIntervalWithinHistoryCutoff,
} from './ReportGeneratorWidget.utils'

type ReportsPageInputs = {
    reportId?: number
    fromDate: Date
    toDate: Date
}

const dateFormat = 'yyyy-MM-dd'

const normalizeReportInputs = (
    values: ReportsPageInputs,
    isRestrictedHistory: boolean,
    historyCutoffDate: Date
): ReportsPageInputs => {
    const today = startOfDay(new Date())
    const normalizedFromDate =
        isRestrictedHistory && isBefore(startOfDay(values.fromDate), historyCutoffDate)
            ? historyCutoffDate
            : isBefore(today, startOfDay(values.fromDate))
              ? today
              : values.fromDate
    const normalizedToDateCandidate = isBefore(today, startOfDay(values.toDate)) ? today : values.toDate
    const normalizedToDate = isBefore(startOfDay(normalizedToDateCandidate), startOfDay(normalizedFromDate))
        ? normalizedFromDate
        : normalizedToDateCandidate

    return {
        reportId: values.reportId,
        fromDate: normalizedFromDate,
        toDate: normalizedToDate,
    }
}

export const ReportsGeneratorWidget = () => {
    const text = useText()
    const { selectedCompanyId, partnerId } = useSelector((state: AdminRootState | PartnerRootState) => state.user)
    const entitlements = useEntitlements()
    const screenSize = useMediaQueryHook()

    const [intervalSelected, setIntervalSelected] = useState<PeriodInterval | undefined>('CurrentWeek')
    const [isGeneratingReport, setIsGeneratingReport] = useState(false)
    const [isHelpInformationDialogVisible, setIsHelpInformationDialogVisible] = useState(false)
    const isRestrictedHistory =
        isAdminDomain && !hasAccessToEntitlement(EntitlementsEnum.REPORTS_EXTENDED_HISTORY, entitlements)
    const historyCutoffDate = useMemo(() => getRestrictedReportHistoryCutoffDate(), [])

    const periodOptions = useMemo<SelectButtonOption<PeriodInterval>[]>(() => {
        return [
            {
                label: text('btnCurrentWeek'),
                value: 'CurrentWeek',
            },
            {
                label: text('btnPreviousWeek'),
                value: 'PreviousWeek',
            },
            {
                label: text('btnCurrentMonth'),
                value: 'CurrentMonth',
            },
            {
                label: text('btnPreviousMonth'),
                value: 'PreviousMonth',
                disabled: isRestrictedHistory && !isIntervalWithinHistoryCutoff('PreviousMonth', historyCutoffDate),
            },
            {
                label: text('btnCurrentYear'),
                value: 'CurrentYear',
                disabled: isRestrictedHistory && !isIntervalWithinHistoryCutoff('CurrentYear', historyCutoffDate),
            },
            {
                label: text('btnPreviousYear'),
                value: 'PreviousYear',
                disabled: isRestrictedHistory && !isIntervalWithinHistoryCutoff('PreviousYear', historyCutoffDate),
            },
        ]
    }, [historyCutoffDate, isRestrictedHistory, text])

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        formState: { isValid },
    } = useForm<ReportsPageInputs>({
        mode: 'onChange',
        defaultValues: {
            reportId: undefined,
            ...getDatesForInterval('CurrentWeek'),
        },
    })

    const { data: adminReports } = useReportGetReportsForAdminQuery(
        {
            selectedCompanyId,
        },
        {
            skip: !isAdminDomain,
        }
    )

    const { data: partnerReports } = useReportGetReportsForPartnerQuery(
        {
            partnerId,
        },
        {
            skip: !isPartnerDomain,
        }
    )

    const reportsOptions = useMemo(() => {
        let reports: Report[]

        if (isAdminDomain && adminReports) {
            reports = adminReports
        } else if (isPartnerDomain && partnerReports) {
            reports = partnerReports
        } else {
            return
        }

        return reports?.map((nextReport) => ({
            value: nextReport.id,
            label: nextReport.name,
        }))
    }, [adminReports, partnerReports])

    const [getReports] = useLazyReportGetReportDataQuery()
    const generateReportCompany = async ({ reportId, fromDate, toDate }: ReportsPageInputs) => {
        try {
            setIsGeneratingReport(true)
            const from = format(fromDate, dateFormat)
            const to = format(addDays(toDate, 1), dateFormat)

            notifySuccess(text('lblGeneratingReport'))

            const tz = jstz.determine()

            const reportData = await getReports({
                reportId,
                companyId: selectedCompanyId,
                fromInLocalTime: from,
                toInLocalTime: to,
                timeZone: tz.name(),
            }).unwrap()

            generateReports(reportData, from, to)
            notifySuccess(text('lblReportGenerateSuccess'))
        } catch (error) {
            notifyError(text('lblReportGenerateFailed'))
        } finally {
            setIsGeneratingReport(false)
        }
    }

    const generateReportPartner = async ({ reportId, fromDate, toDate }: ReportsPageInputs) => {
        try {
            setIsGeneratingReport(true)
            const from = format(fromDate, dateFormat)
            const to = format(addDays(toDate, 1), dateFormat)

            notifySuccess(text('lblGeneratingReport'))

            const tz = jstz.determine()

            const reportData = await getReports({
                reportId,
                partnerId,
                fromInLocalTime: from,
                toInLocalTime: to,
                timeZone: tz.name(),
            }).unwrap()

            generateReports(reportData, from, to)
            notifySuccess(text('lblReportGenerateSuccess'))
        } catch (error) {
            notifyError(text('lblReportGenerateFailed'))
        } finally {
            setIsGeneratingReport(false)
        }
    }
    const getReportDescription = () => {
        const reports = isAdminDomain ? adminReports : partnerReports

        return reports?.find((report) => report.id === reportId)?.description
    }

    const reportId = watch('reportId')
    const fromDate = watch('fromDate')
    const toDate = watch('toDate')

    useEffect(() => {
        const nextValues = normalizeReportInputs(getValues(), isRestrictedHistory, historyCutoffDate)

        if (nextValues.fromDate.getTime() !== fromDate.getTime()) {
            setValue('fromDate', nextValues.fromDate, { shouldDirty: true, shouldValidate: true })
        }

        if (nextValues.toDate.getTime() !== toDate.getTime()) {
            setValue('toDate', nextValues.toDate, { shouldDirty: true, shouldValidate: true })
        }

        if (intervalSelected && periodOptions.find((option) => option.value === intervalSelected)?.disabled) {
            setIntervalSelected(undefined)
        }
    }, [fromDate, getValues, historyCutoffDate, intervalSelected, isRestrictedHistory, periodOptions, setValue, toDate])

    const setDateBasedOnInterval = (interval: PeriodInterval) => {
        if (periodOptions.find((option) => option.value === interval)?.disabled) {
            return
        }

        const dateInterval = getDatesForInterval(interval)

        if (!dateInterval) return

        if (dateInterval.fromDate) {
            setValue('fromDate', dateInterval.fromDate, { shouldDirty: true, shouldValidate: true })
        }

        if (dateInterval.toDate) {
            setValue('toDate', dateInterval.toDate, { shouldDirty: true, shouldValidate: true })
        }

        setIntervalSelected(interval)
    }

    return (
        <>
            <WidgetLayout
                title={text('lblReportGenerator')}
                headerButtons={
                    <Button
                        type="button"
                        icon="pi pi-question-circle"
                        aria-label={text('btnHelp')}
                        tooltip={text('btnHelp')}
                        text
                        rounded
                        onClick={() => setIsHelpInformationDialogVisible(true)}
                    />
                }
                actionButtons={
                    <Button
                        type="button"
                        onClick={handleSubmit((values) =>
                            isAdminDomain
                                ? generateReportCompany(
                                      normalizeReportInputs(values, isRestrictedHistory, historyCutoffDate)
                                  )
                                : generateReportPartner(
                                      normalizeReportInputs(values, isRestrictedHistory, historyCutoffDate)
                                  )
                        )}
                        label={text('btnGenerate')}
                        icon="pi pi-file-export"
                        disabled={!isValid}
                        loading={isGeneratingReport}
                        data-testid="generate-btn"
                    />
                }
            >
                <div className="flex flex-wrap md:flex-nowrap gap-3 field">
                    <div className="w-full w-12 md:w-6">
                        <Controller
                            name="reportId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <Combobox
                                    id="selecter-report"
                                    data-testid="select-report-dropdown"
                                    options={reportsOptions}
                                    value={value}
                                    onChange={(option) => onChange(option.value)}
                                    labelText={text('lblReport')}
                                />
                            )}
                        />
                    </div>
                    <div className="w-12 md:w-6 flex gap-2">
                        <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) =>
                                    !isRestrictedHistory ||
                                    !isBefore(startOfDay(value), historyCutoffDate) ||
                                    text('msgReportsHistoryRestrictionValidation', {
                                        days: REPORT_HISTORY_RESTRICTION_DAYS,
                                    }),
                            }}
                            render={({ field: { onChange, value } }) => (
                                <DatePicker
                                    id="fromDate"
                                    data-testid="fromDate-input"
                                    wrapperClassName="w-6"
                                    labelText={text('lblFromField')}
                                    min={isRestrictedHistory ? format(historyCutoffDate, dateFormat) : undefined}
                                    max={format(
                                        isBefore(startOfDay(new Date()), startOfDay(toDate)) ? new Date() : toDate,
                                        dateFormat
                                    )}
                                    value={format(value, dateFormat)}
                                    onChange={(event) => {
                                        const nextFromDate = event.target.valueAsDate

                                        if (!nextFromDate) {
                                            return
                                        }

                                        const normalizedFromDate =
                                            isRestrictedHistory &&
                                            isBefore(startOfDay(nextFromDate), startOfDay(historyCutoffDate))
                                                ? historyCutoffDate
                                                : nextFromDate

                                        onChange(normalizedFromDate)

                                        if (isBefore(startOfDay(toDate), startOfDay(normalizedFromDate))) {
                                            setValue('toDate', normalizedFromDate, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            })
                                        }

                                        setIntervalSelected(undefined)
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="toDate"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <DatePicker
                                    id="toDate"
                                    data-testid="toDate-input"
                                    wrapperClassName="w-6"
                                    labelText={text('lblToField')}
                                    min={format(fromDate, dateFormat)}
                                    max={format(new Date(), dateFormat)}
                                    value={format(value, dateFormat)}
                                    onChange={(event) => {
                                        onChange(event.target.valueAsDate)
                                        setIntervalSelected(undefined)
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>

                {isRestrictedHistory && (
                    <div className="my-3 text-sm text-gray-600" data-testid="report-history-restriction">
                        <p className="flex gap-1">
                            {`${text('msgReportsHistoryRestriction', { days: REPORT_HISTORY_RESTRICTION_DAYS })}`}

                            <Link to="/billing" className="p-0" data-testid="report-history-upgrade-link">
                                {text('lblUpgradePlan')}
                            </Link>
                        </p>
                    </div>
                )}

                <div>
                    <div className="flex justify-content-between mb-2">
                        <SLabelText className="label-indent" data-testid="earthingSystem-label">
                            {text('lblPreDefinedIntervals')}
                        </SLabelText>
                    </div>
                    {screenSize === ScreenDeviceType.Tablet ? (
                        <Combobox
                            enableDefaultOption={false}
                            id="interval"
                            data-testid="interval-dropdown"
                            options={periodOptions}
                            value={intervalSelected}
                            onChange={(option) => setDateBasedOnInterval(option.value)}
                        />
                    ) : (
                        <SelectButton<PeriodInterval>
                            id="pre-defined-intervals"
                            ariaLabel={text('lblPreDefinedIntervals')}
                            options={periodOptions}
                            onChange={(value) => setDateBasedOnInterval(value)}
                            value={intervalSelected}
                        />
                    )}
                </div>
                {reportId && (
                    <div className="mt-2 mb-4">
                        <div className="flex justify-content-between mb-2">
                            <SLabelText className="label-indent" data-testid="earthingSystem-label">
                                {text('lblSelectedReportDescription')}
                            </SLabelText>
                        </div>
                        <div>
                            <i>{getReportDescription()}</i>
                        </div>
                    </div>
                )}
            </WidgetLayout>
            <InformationDialog
                visible={isHelpInformationDialogVisible}
                title={text('lblReportHelpDialogTitle')}
                message={<Text code="msgImportingReports" />}
                onHide={() => setIsHelpInformationDialogVisible(false)}
            />
        </>
    )
}
